import { useCallback, useEffect, useState } from 'react'
import { ContactCompany } from 'models/companies'
import { EmailMessage } from 'models/emailIntegration'
import { Modal } from 'components/UI/Modal'
import { EmailsModalHeader, EmailTab, ModalView } from './EmailsModalHeader'
import { EmailsListView } from './EmailsListView'
import { EmailDetailView } from './EmailDetailView'

interface BaseEmailsModalProps {
  show: boolean
  onHide: () => void
  forDoorProxyId?: number
  companyName?: string
  /** Initial tab selection (defaults to 'received') */
  initialTab?: EmailTab
}

interface EmailsListModalProps extends BaseEmailsModalProps {
  /** When true, shows only the email view without list navigation */
  emailOnlyMode?: false
  contactCompany: ContactCompany
  /** If provided, opens directly to this email */
  initialEmail?: EmailMessage
}

interface EmailOnlyModalProps extends BaseEmailsModalProps {
  /** When true, shows only the email view without list navigation */
  emailOnlyMode: true
  contactCompany?: never
  /** The email to display (required in emailOnlyMode) */
  initialEmail: EmailMessage
}

type EmailsModalProps = EmailsListModalProps | EmailOnlyModalProps

export function EmailsModal(props: EmailsModalProps) {
  const {
    show,
    onHide,
    forDoorProxyId,
    companyName,
    initialEmail,
    initialTab = 'received',
    emailOnlyMode,
  } = props

  const contactCompany = emailOnlyMode ? undefined : props.contactCompany

  const [view, setView] = useState<ModalView>(
    emailOnlyMode || initialEmail ? 'email' : 'list'
  )
  const [selectedTab, setSelectedTab] = useState<EmailTab>(initialTab)
  const [selectedEmail, setSelectedEmail] = useState<EmailMessage | undefined>(
    initialEmail
  )
  const [emailsList, setEmailsList] = useState<EmailMessage[]>([])

  // Find current email index in the list
  const currentIndex = selectedEmail
    ? emailsList.findIndex((e) => e.id === selectedEmail.id)
    : -1
  const hasPrev = !emailOnlyMode && currentIndex > 0
  const hasNext = !emailOnlyMode && currentIndex < emailsList.length - 1

  const handlePrevEmail = useCallback(() => {
    if (hasPrev) {
      setSelectedEmail(emailsList[currentIndex - 1])
    }
  }, [hasPrev, emailsList, currentIndex])

  const handleNextEmail = useCallback(() => {
    if (hasNext) {
      setSelectedEmail(emailsList[currentIndex + 1])
    }
  }, [hasNext, emailsList, currentIndex])

  const handleEmailsChange = useCallback((emails: EmailMessage[]) => {
    setEmailsList(emails)
  }, [])

  // Handle initial email navigation
  useEffect(() => {
    if (show && initialEmail) {
      setSelectedEmail(initialEmail)
      setView('email')
      // Set tab based on email type
      setSelectedTab(initialEmail.outbound ? 'sent' : 'received')
    }
  }, [show, initialEmail])

  // Reset state when modal closes
  useEffect(() => {
    if (!show) {
      setView(emailOnlyMode ? 'email' : 'list')
      setSelectedEmail(emailOnlyMode ? initialEmail : undefined)
      setSelectedTab(initialTab)
      setEmailsList([])
    }
  }, [show, initialTab, emailOnlyMode, initialEmail])

  const handleEmailClick = (email: EmailMessage) => {
    setSelectedEmail(email)
    setView('email')
  }

  const handleBreadcrumbClick = () => {
    setView('list')
    setSelectedEmail(undefined)
  }

  const displayName = companyName || contactCompany?.name

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      centered
      enforceFocus={false}
      backdrop="static"
    >
      <button
        type="button"
        className="btn-close absolute top-5 right-[30px] z-10"
        aria-label="Close"
        onClick={onHide}
      />
      <EmailsModalHeader
        companyName={displayName}
        view={view}
        activeTab={selectedTab}
        emailSubject={selectedEmail?.subject}
        onBreadcrumbClick={emailOnlyMode ? undefined : handleBreadcrumbClick}
        emailOnlyMode={emailOnlyMode}
      />

      <Modal.Body className="px-[32px] pb-[24px] pt-0">
        <div
          style={{ height: '60vh', display: 'flex', flexDirection: 'column' }}
        >
          {view === 'list' && contactCompany ? (
            <EmailsListView
              contactCompanyId={contactCompany.id}
              forDoorProxyId={forDoorProxyId}
              selectedTab={selectedTab}
              onTabChange={setSelectedTab}
              onEmailClick={handleEmailClick}
              onEmailsChange={handleEmailsChange}
            />
          ) : (
            selectedEmail && (
              <EmailDetailView
                email={selectedEmail}
                onPrev={hasPrev ? handlePrevEmail : undefined}
                onNext={hasNext ? handleNextEmail : undefined}
              />
            )
          )}
        </div>
      </Modal.Body>
    </Modal>
  )
}
